import { Alert, Button, Input } from "@simplicity/ui";
import { detectPlaceholders } from "./detectPlaceholders";

interface ParamsEditorProps {
  params: Record<string, string>;
  onChange: (params: Record<string, string>) => void;
  sqlTemplate: string;
}

export function ParamsEditor({ params, onChange, sqlTemplate }: ParamsEditorProps) {
  const detected = detectPlaceholders(sqlTemplate);
  const missing = detected.filter((p) => !(p in params));
  const entries = Object.entries(params);

  function updateKey(oldKey: string, newKeyVal: string) {
    const updated: Record<string, string> = {};
    for (const [k, v] of entries) {
      updated[k === oldKey ? newKeyVal : k] = v;
    }
    onChange(updated);
  }

  function updateValue(key: string, value: string) {
    onChange({ ...params, [key]: value });
  }

  function addParam(key: string) {
    onChange({ ...params, [key]: "" });
  }

  return (
    <div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="w-1/2 py-1.5 font-medium">Name</th>
            <th className="w-1/2 py-1.5 font-medium">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-border/50">
            <td className="py-1 pr-2">
              {/* SIM-2016: display-only readonly inputs need an accessible
                  name (WCAG 4.1.2 / 1.3.1) — there is no visible <Label>. */}
              <Input
                value="gcpProjectId"
                readOnly
                aria-label="Auto-provided parameter name"
                className="font-mono text-sm bg-muted"
              />
            </td>
            <td className="py-1 pr-2">
              <Input
                value="(auto-provided)"
                readOnly
                aria-label="gcpProjectId value (auto-provided)"
                className="font-mono text-sm bg-muted text-muted-foreground italic"
              />
            </td>
          </tr>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-border/50">
              <td className="py-1 pr-2">
                <Input
                  value={key}
                  onChange={(e) => updateKey(key, e.target.value)}
                  placeholder="Key"
                  className="font-mono text-sm"
                />
              </td>
              <td className="py-1 pr-2">
                <Input
                  value={value}
                  onChange={(e) => updateValue(key, e.target.value)}
                  placeholder="Value"
                  className="font-mono text-sm"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {missing.length > 0 && (
        <Alert variant="warning" className="mt-2 text-xs">
          Missing values for:{" "}
          {missing.map((p, i) => (
            <span key={p}>
              {i > 0 && ", "}
              <code className="rounded bg-warning/20 px-1">{p}</code>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="ml-1 h-auto p-0 text-foreground"
                onClick={() => addParam(p)}
              >
                Add
              </Button>
            </span>
          ))}
        </Alert>
      )}
    </div>
  );
}
